import { Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, Textarea } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

// --- Generic Confirmation Modal ---

type ConfirmationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  header: string;
  body: string;
  cancelLabel: string;
  confirmLabel: string;
  onConfirm: () => void;
  isLoading?: boolean;
};

const ConfirmationModal = ({ isOpen, onClose, header, body, cancelLabel, confirmLabel, onConfirm, isLoading }: ConfirmationModalProps) => (
  <Modal closeOnOverlayClick={false} isOpen={isOpen} onClose={onClose}>
    <ModalOverlay />
    <ModalContent>
      <ModalHeader>{header}</ModalHeader>
      <ModalCloseButton />
      <ModalBody pb={6}>{body}</ModalBody>
      <ModalFooter>
        <Button onClick={onClose} mr={3} variant="outline">
          {cancelLabel}
        </Button>
        <Button colorScheme="blue" onClick={onConfirm} isLoading={isLoading}>
          {confirmLabel}
        </Button>
      </ModalFooter>
    </ModalContent>
  </Modal>
);

// --- Reason Modal (with textarea) ---

type ReasonModalProps = {
  isOpen: boolean;
  onClose: () => void;
  header: string;
  description: string;
  placeholder: string;
  cancelLabel: string;
  confirmLabel: string;
  reason: string;
  onReasonChange: (reason: string) => void;
  onConfirm: () => void;
  isLoading?: boolean;
};

const ReasonModal = ({
  isOpen,
  onClose,
  header,
  description,
  placeholder,
  cancelLabel,
  confirmLabel,
  reason,
  onReasonChange,
  onConfirm,
  isLoading,
}: ReasonModalProps) => (
  <Modal closeOnOverlayClick={false} isOpen={isOpen} onClose={onClose}>
    <ModalOverlay />
    <ModalContent maxW="600px" w="100%">
      <ModalHeader>{header}</ModalHeader>
      <ModalCloseButton />
      <ModalBody pb={6}>
        <Text mb={4}>{description}</Text>
        <Textarea placeholder={placeholder} value={reason} onChange={(e) => onReasonChange(e.target.value)} maxLength={255} minHeight="150px" />
      </ModalBody>
      <ModalFooter>
        <Button onClick={onClose} mr={3} variant="outline">
          {cancelLabel}
        </Button>
        <Button colorScheme="blue" onClick={onConfirm} disabled={reason.trim().length === 0} isLoading={isLoading}>
          {confirmLabel}
        </Button>
      </ModalFooter>
    </ModalContent>
  </Modal>
);

// --- All Verification Modals ---

export type VerificationModalsProps = {
  // Reject
  isRejectOpen: boolean;
  onRejectClose: () => void;
  onRejectConfirm: () => void;
  isRejectLoading: boolean;
  // Approve
  isApproveOpen: boolean;
  onApproveClose: () => void;
  onApproveConfirm: () => void;
  isApproveLoading: boolean;
  isOnStepOne: boolean;
  // Document replacement confirmations
  isIdReplacementOpen: boolean;
  onIdReplacementClose: () => void;
  onIdReplacementConfirm: () => void;
  isBlReplacementOpen: boolean;
  onBlReplacementClose: () => void;
  onBlReplacementConfirm: () => void;
  isPoaReplacementOpen: boolean;
  onPoaReplacementClose: () => void;
  onPoaReplacementConfirm: () => void;
  isHandelskarteReplacementOpen: boolean;
  onHandelskarteReplacementClose: () => void;
  onHandelskarteReplacementConfirm: () => void;
  // Verified replacement (with reason)
  isVerifiedReplacementOpen: boolean;
  onVerifiedReplacementClose: () => void;
  onVerifiedReplacementConfirm: () => void;
  reasonForReplacement: string;
  onReasonChange: (reason: string) => void;
  // Clarification replacement (with reason)
  isClarificationReplacementOpen: boolean;
  onClarificationReplacementClose: () => void;
  onClarificationReplacementConfirm: () => void;
};

const VerificationModals = ({
  isRejectOpen,
  onRejectClose,
  onRejectConfirm,
  isRejectLoading,
  isApproveOpen,
  onApproveClose,
  onApproveConfirm,
  isApproveLoading,
  isOnStepOne,
  isIdReplacementOpen,
  onIdReplacementClose,
  onIdReplacementConfirm,
  isBlReplacementOpen,
  onBlReplacementClose,
  onBlReplacementConfirm,
  isPoaReplacementOpen,
  onPoaReplacementClose,
  onPoaReplacementConfirm,
  isHandelskarteReplacementOpen,
  onHandelskarteReplacementClose,
  onHandelskarteReplacementConfirm,
  isVerifiedReplacementOpen,
  onVerifiedReplacementClose,
  onVerifiedReplacementConfirm,
  reasonForReplacement,
  onReasonChange,
  isClarificationReplacementOpen,
  onClarificationReplacementClose,
  onClarificationReplacementConfirm,
}: VerificationModalsProps) => {
  const { t } = useTranslation();

  return (
    <>
      {/* Reject Modal */}
      <ConfirmationModal
        isOpen={isRejectOpen}
        onClose={onRejectClose}
        header={t('workshop_verification_request:reject_verification.modal.header')}
        body={t('workshop_verification_request:reject_verification.modal.description')}
        cancelLabel={t('workshop_verification_request:button.discard')}
        confirmLabel={t('workshop_verification_request:button.confirm')}
        onConfirm={onRejectConfirm}
        isLoading={isRejectLoading}
      />

      {/* Approve Modal */}
      <ConfirmationModal
        isOpen={isApproveOpen}
        onClose={onApproveClose}
        header={t('workshop_verification_request:approve.modal.header')}
        body={
          isOnStepOne
            ? t('workshop_verification_request:approve.modal.description_step_one')
            : t('workshop_verification_request:approve.modal.description_step_two')
        }
        cancelLabel={t('workshop_verification_request:button.discard')}
        confirmLabel={t('workshop_verification_request:button.confirm')}
        onConfirm={onApproveConfirm}
        isLoading={isApproveLoading}
      />

      {/* ID Replacement Confirmation */}
      <ConfirmationModal
        isOpen={isIdReplacementOpen}
        onClose={onIdReplacementClose}
        header={t('workshop_verification_request:identification.label')}
        body={t('workshop_verification_request:doc_replacement_check')}
        cancelLabel={t('common:cancel')}
        confirmLabel={t('workshop_verification_request:button.confirm')}
        onConfirm={onIdReplacementConfirm}
      />

      {/* BL Replacement Confirmation */}
      <ConfirmationModal
        isOpen={isBlReplacementOpen}
        onClose={onBlReplacementClose}
        header={t('workshop_verification_request:business_licence.label')}
        body={t('workshop_verification_request:doc_replacement_check')}
        cancelLabel={t('common:cancel')}
        confirmLabel={t('workshop_verification_request:replace')}
        onConfirm={onBlReplacementConfirm}
      />

      {/* Handwerkskarte Replacement Confirmation */}
      <ConfirmationModal
        isOpen={isHandelskarteReplacementOpen}
        onClose={onHandelskarteReplacementClose}
        header={t('workshop_verification_request:handwerkskarte.label')}
        body={t('workshop_verification_request:doc_replacement_check')}
        cancelLabel={t('common:cancel')}
        confirmLabel={t('workshop_verification_request:replace')}
        onConfirm={onHandelskarteReplacementConfirm}
      />

      {/* PoA Replacement Confirmation */}
      <ConfirmationModal
        isOpen={isPoaReplacementOpen}
        onClose={onPoaReplacementClose}
        header={t('workshop_verification_request:power_of_attorney.label')}
        body={t('workshop_verification_request:doc_replacement_check')}
        cancelLabel={t('common:cancel')}
        confirmLabel={t('workshop_verification_request:replace')}
        onConfirm={onPoaReplacementConfirm}
      />

      {/* Verified Replacement (with reason) */}
      <ReasonModal
        isOpen={isVerifiedReplacementOpen}
        onClose={onVerifiedReplacementClose}
        header={t('workshop_verification_request:replace_documents_reason.verified.header')}
        description={t('workshop_verification_request:replace_documents_reason.verified.description')}
        placeholder={t('workshop_verification_request:replace_documents_reason.verified.placeholder')}
        cancelLabel={t('common:cancel')}
        confirmLabel={t('workshop_verification_request:replace')}
        reason={reasonForReplacement}
        onReasonChange={onReasonChange}
        onConfirm={onVerifiedReplacementConfirm}
      />

      {/* Clarification Replacement (with reason) */}
      <ReasonModal
        isOpen={isClarificationReplacementOpen}
        onClose={onClarificationReplacementClose}
        header={t('workshop_verification_request:replace_documents_reason.clarification.header')}
        description={t('workshop_verification_request:replace_documents_reason.clarification.description')}
        placeholder={t('workshop_verification_request:replace_documents_reason.clarification.placeholder')}
        cancelLabel={t('common:cancel')}
        confirmLabel={t('workshop_verification_request:submit_for_verification')}
        reason={reasonForReplacement}
        onReasonChange={onReasonChange}
        onConfirm={onClarificationReplacementConfirm}
      />
    </>
  );
};

export default VerificationModals;
